import React, { useRef, useState } from 'react';
import { addStudentMemo, deleteStudentMemo, newMemoClientId, saveStudentProfile, updateStudentMemo } from '../../api/classRosterApi';
import { useToast } from '../../components/Toast';
import { trackClassRosterEvent } from '../../utils/eventTracker';
import { MEMO_AREAS, MEMO_MAX_CHARS, restrictedMemoKinds, suggestMemoArea } from '../../utils/studentMemos';
import './StudentNotes.css';

const formatDay = (value) => {
  const [, month, day] = String(value || '').split('-').map(Number);
  return month && day ? `${month}월 ${day}일` : '';
};

const sortMemos = memos => [...memos].sort((a, b) => (
  String(b.date || '').localeCompare(String(a.date || '')) || String(b.created_at || '').localeCompare(String(a.created_at || ''))
));

const MemoMeta = ({ memo }) => (
  <span className="student-notes-meta">
    {formatDay(memo.date)}
    {memo.area && <span className="student-notes-area">{memo.area}</span>}
    {memo.source === 'group' && <span className="student-notes-area">함께</span>}
  </span>
);

const OtherYearMemos = ({ memos }) => {
  const [open, setOpen] = useState(false);
  if (!memos?.length) return null;
  return (
    <div className="student-notes-other" data-testid="student-other-year-memos">
      <button type="button" className="student-notes-other-toggle" aria-expanded={open} onClick={() => setOpen(v => !v)}>
        다른 학년도 메모 {memos.length}개 · 생기부에 넣지 않아요
        <span aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <ul className="student-notes-list is-other">
          {memos.map(memo => (
            <li key={memo.id}>
              <div className="student-notes-memo is-static">
                <span className="student-notes-meta">
                  {memo.school_year ? `${memo.school_year}학년도 · ` : ''}{formatDay(memo.date)}
                  {memo.area && <span className="student-notes-area">{memo.area}</span>}
                </span>
                <span className="student-notes-text">{memo.text}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RestrictedHint = ({ text }) => {
  const kinds = restrictedMemoKinds(text);
  if (kinds.length === 0) return null;
  return <span className="student-notes-hint">{kinds.join('·')} 내용은 생기부 문장에 쓸 수 없어요. 메모로만 남겨 두세요.</span>;
};

const ProfileRow = ({ label, value, placeholder, editing, onEdit, children }) => (
  <div className="student-notes-profile-row">
    <span className="student-notes-profile-label">{label}</span>
    {editing ? children : (
      <button type="button" className={`student-notes-profile-value${value ? '' : ' is-empty'}`} onClick={onEdit}>
        {value || placeholder}
      </button>
    )}
  </div>
);

/** 학생 화면 위쪽: 고정 정보 두 줄(임원·진로희망)과 메모. 상태는 부모(notes)가 들고 있다. */
const scoreText = (item) => {
  if (item.blank) return '빈 답안';
  if (item.score === null || item.score === undefined) return '';
  return item.max_score ? `${item.score}/${item.max_score}점` : `${item.score}점`;
};

const isoDay = value => String(value || '').slice(0, 10);

const EvidenceItem = ({ item }) => (
  <div className="student-notes-memo is-static is-evidence">
    <span className="student-notes-meta">
      {formatDay(isoDay(item.date))}
      <span className="student-notes-area is-evidence">채점</span>
      <span className="student-notes-evidence-title">{item.title}</span>
      {scoreText(item) && <span>{scoreText(item)}</span>}
    </span>
    {item.line && <span className="student-notes-text">{item.line}</span>}
  </div>
);

/**
 * 메모와 채점 근거를 날짜순(최근 먼저) 한 줄로. 채점 근거는 읽기 전용이고, 생기부로 넘길 때 고르면 들어간다.
 * evidence: 학생별 기록의 채점 항목(이름이 명단과 맞은 결과).
 */
const StudentNotes = ({ rosterId, sid, notes, evidence = [], onChange }) => {
  const { showToast } = useToast();
  const profile = notes.profile;
  const memos = notes.memos;
  const [editingProfile, setEditingProfile] = useState('');
  const [profileDraft, setProfileDraft] = useState(profile);
  const [draft, setDraft] = useState('');
  const [area, setArea] = useState(null);
  const [editing, setEditing] = useState(null);
  // 임원·진로희망은 한 문서라, 앞 저장이 끝나기 전에 다음 저장이 먼저 도착하면 앞 값이 지워진다. 순서대로 보낸다.
  const profileQueueRef = useRef(Promise.resolve());
  const profileSeqRef = useRef(0);

  const timeline = [
    ...memos.map(memo => ({ kind: 'memo', day: memo.date || '', at: memo.created_at || '', memo })),
    ...evidence.map(item => ({ kind: 'scoring', day: isoDay(item.date), at: item.date || '', item })),
  ].sort((a, b) => b.day.localeCompare(a.day) || String(b.at).localeCompare(String(a.at)));

  const suggested = suggestMemoArea(draft);
  const chosenArea = area === null ? suggested : area;

  const openProfile = (field) => {
    setProfileDraft(profile);
    setEditingProfile(field);
  };

  const saveProfile = () => {
    const next = profileDraft;
    const previous = profile;
    const seq = profileSeqRef.current + 1;
    profileSeqRef.current = seq;
    onChange(prev => ({ ...prev, profile: next }));
    setEditingProfile('');
    profileQueueRef.current = profileQueueRef.current
      .then(() => saveStudentProfile(rosterId, sid, next))
      .then((saved) => {
        if (profileSeqRef.current === seq) onChange(prev => ({ ...prev, profile: saved }));
        trackClassRosterEvent('student_profile_saved', { roster_id: rosterId, has_role: Boolean(saved.role), has_career: Boolean(saved.career) });
      })
      .catch((error) => {
        if (profileSeqRef.current === seq) onChange(prev => ({ ...prev, profile: previous }));
        showToast(error.message || '저장하지 못했어요. 다시 시도해 주세요.', 'error');
      });
  };

  const addMemo = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    const memoArea = chosenArea;
    setDraft('');
    setArea(null);
    try {
      const memo = await addStudentMemo(rosterId, sid, { text, area: memoArea, clientId: newMemoClientId() });
      onChange(prev => ({ ...prev, memos: sortMemos([memo, ...prev.memos]) }));
      trackClassRosterEvent('student_memo_added', {
        roster_id: rosterId,
        memo_count: memos.length + 1,
        memo_chars: text.length,
        has_area: Boolean(memoArea),
        has_restricted: restrictedMemoKinds(text).length > 0,
      });
    } catch (error) {
      setDraft(current => current || text);
      showToast(error.message || '메모를 남기지 못했어요. 다시 시도해 주세요.', 'error');
    }
  };

  const saveEdit = async () => {
    const text = editing.text.trim();
    if (!text) return;
    try {
      const memo = await updateStudentMemo(editing.id, { text, area: editing.area });
      onChange(prev => ({ ...prev, memos: prev.memos.map(item => (item.id === memo.id ? memo : item)) }));
      trackClassRosterEvent('student_memo_edited', { roster_id: rosterId, memo_chars: text.length });
      setEditing(null);
    } catch (error) {
      showToast(error.message || '고치지 못했어요. 다시 시도해 주세요.', 'error');
    }
  };

  const removeMemo = async (memo) => {
    try {
      await deleteStudentMemo(memo.id);
      onChange(prev => ({ ...prev, memos: prev.memos.filter(item => item.id !== memo.id) }));
      trackClassRosterEvent('student_memo_deleted', { roster_id: rosterId, memo_count: memos.length - 1 });
      setEditing(null);
      const clientId = newMemoClientId();
      showToast('메모를 지웠어요', 'info', 5000, {
        label: '되돌리기',
        onClick: async () => {
          try {
            const restored = await addStudentMemo(rosterId, sid, { text: memo.text, area: memo.area, date: memo.date, clientId });
            onChange(prev => ({ ...prev, memos: sortMemos([restored, ...prev.memos.filter(item => item.id !== restored.id)]) }));
          } catch (error) {
            showToast(error.message || '되돌리지 못했어요. 다시 시도해 주세요.', 'error');
          }
        },
      });
    } catch (error) {
      showToast(error.message || '지우지 못했어요. 다시 시도해 주세요.', 'error');
    }
  };

  const renderMemo = memo => (editing?.id === memo.id ? (
    <div className="student-notes-edit">
      <textarea
        aria-label="메모 고치기"
        rows={3}
        maxLength={MEMO_MAX_CHARS}
        value={editing.text}
        onChange={event => setEditing(prev => ({ ...prev, text: event.target.value }))}
      />
      <div className="student-notes-edit-actions">
        <button type="button" className="student-notes-delete" onClick={() => removeMemo(memo)}>지우기</button>
        <span />
        <button type="button" className="student-notes-cancel" onClick={() => setEditing(null)}>취소</button>
        <button type="button" className="student-notes-save" onClick={saveEdit} disabled={!editing.text.trim()}>저장</button>
      </div>
    </div>
  ) : (
    <button type="button" className="student-notes-memo" onClick={() => setEditing({ id: memo.id, text: memo.text, area: memo.area })}>
      <MemoMeta memo={memo} />
      <span className="student-notes-text">{memo.text}</span>
      <RestrictedHint text={memo.text} />
    </button>
  ));

  const profileForm = (fields) => (
    <form className="student-notes-profile-form" onSubmit={(event) => { event.preventDefault(); saveProfile(); }}>
      {fields.map(([key, placeholder]) => (
        <input
          key={key}
          aria-label={placeholder}
          value={profileDraft[key] || ''}
          maxLength={key === 'career' ? 80 : 40}
          placeholder={placeholder}
          onChange={event => setProfileDraft(prev => ({ ...prev, [key]: event.target.value }))}
        />
      ))}
      <button type="submit" className="student-notes-save">저장</button>
      <button type="button" className="student-notes-cancel" onClick={() => setEditingProfile('')}>취소</button>
    </form>
  );

  return (
    <section className="student-notes" data-testid="student-notes">
      <div className="student-notes-profile">
        <ProfileRow
          label="임원"
          value={profile.role ? `${profile.role}${profile.role_period ? ` · ${profile.role_period}` : ''}` : ''}
          placeholder="없음"
          editing={editingProfile === 'role'}
          onEdit={() => openProfile('role')}
        >
          {profileForm([['role', '예: 학급 회장'], ['role_period', '예: 1학기']])}
        </ProfileRow>
        <ProfileRow
          label="진로희망"
          value={profile.career}
          placeholder="입력하기"
          editing={editingProfile === 'career'}
          onEdit={() => openProfile('career')}
        >
          {profileForm([['career', '예: 수의사']])}
        </ProfileRow>
      </div>

      <h2 className="class-students-section">메모{memos.length > 0 ? ` ${memos.length}` : ''}</h2>
      <form className="student-notes-input" onSubmit={addMemo}>
        <textarea
          aria-label="메모"
          rows={draft ? 3 : 1}
          maxLength={MEMO_MAX_CHARS}
          value={draft}
          placeholder="오늘 본 모습을 적어 두세요"
          onChange={event => setDraft(event.target.value)}
        />
        <button type="submit" className="classes-btn-primary" disabled={!draft.trim()}>남기기</button>
      </form>
      {draft.trim() && (
        <>
          <div className="student-notes-areas" role="group" aria-label="영역">
            {MEMO_AREAS.map(item => (
              <button
                key={item}
                type="button"
                aria-pressed={chosenArea === item}
                className={chosenArea === item ? 'is-active' : ''}
                onClick={() => setArea(chosenArea === item ? '' : item)}
              >
                {item}
              </button>
            ))}
          </div>
          <RestrictedHint text={draft} />
        </>
      )}

      {timeline.length > 0 && (
        <ul className="student-notes-list" data-testid="student-memo-list">
          {timeline.map(entry => (entry.kind === 'scoring' ? (
            <li key={`scoring-${entry.item.record_id}-${entry.item.date}`} data-testid="student-evidence-item">
              <EvidenceItem item={entry.item} />
            </li>
          ) : (
            <li key={entry.memo.id}>{renderMemo(entry.memo)}</li>
          )))}
        </ul>
      )}
      <OtherYearMemos memos={notes.otherYearMemos} />
    </section>
  );
};

export default StudentNotes;
